import { useLayoutEffect, useRef } from "react";
import Input from "@components/Form/Input/Input";

// Swedish personnummer: 6-digit (YYMMDD) or 8-digit (YYYYMMDD) date, then a
// 4-digit suffix. Long format is only distinguishable once the digit count
// exceeds what the short format's 6+4 = 10 digits can hold.
const SHORT_DATE_LEN = 6;
const LONG_DATE_LEN = 8;
const SUFFIX_LEN = 4;
const SHORT_TOTAL_LEN = SHORT_DATE_LEN + SUFFIX_LEN;
const MAX_DIGITS = LONG_DATE_LEN + SUFFIX_LEN;

// Strips any manually typed dashes/spaces and re-inserts a single dash after
// the date part, e.g. "02-0304--1234" -> "020304-1234" and
// "1990-0304-1234" -> "19900304-1234".
export const formatPersonalIdentityNumber = (raw) => {
  const digits = String(raw ?? "")
    .replace(/\D/g, "")
    .slice(0, MAX_DIGITS);
  const dateLen =
    digits.length > SHORT_TOTAL_LEN ? LONG_DATE_LEN : SHORT_DATE_LEN;
  if (digits.length <= dateLen) return digits;
  return `${digits.slice(0, dateLen)}-${digits.slice(dateLen, dateLen + SUFFIX_LEN)}`;
};

const countDigits = (str) => (str.match(/\d/g) || []).length;

// Maps "N digits precede the caret" to a character offset in the formatted
// string, so the dash inserted by formatting doesn't shift the caret.
const caretOffsetForDigitCount = (formatted, digitCount) => {
  if (digitCount <= 0) return 0;
  let seen = 0;
  for (let i = 0; i < formatted.length; i++) {
    if (/\d/.test(formatted[i])) seen++;
    if (seen === digitCount) return i + 1;
  }
  return formatted.length;
};

const PersonalIdentityNumberInput = ({
  value,
  onChange = () => {},
  placeholder = "YYMMDD-XXXX",
  ...props
}) => {
  const wrapperRef = useRef(null);
  const pendingCaretRef = useRef(null);

  const handleChange = (val) => {
    const inputEl = wrapperRef.current?.querySelector("input");
    const caretPos = inputEl?.selectionStart ?? val.length;
    const digitsBeforeCaret = countDigits(val.slice(0, caretPos));

    const formatted = formatPersonalIdentityNumber(val);
    pendingCaretRef.current = caretOffsetForDigitCount(
      formatted,
      digitsBeforeCaret,
    );
    onChange(formatted);
  };

  // Runs after the parent passes the reformatted value back down, restoring
  // the caret to where the user was actually typing/editing.
  useLayoutEffect(() => {
    if (pendingCaretRef.current === null) return;
    const inputEl = wrapperRef.current?.querySelector("input");
    inputEl?.setSelectionRange(
      pendingCaretRef.current,
      pendingCaretRef.current,
    );
    pendingCaretRef.current = null;
  }, [value]);

  return (
    <div ref={wrapperRef}>
      <Input
        {...props}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        placeholder={placeholder}
        maxLength={MAX_DIGITS + 1}
        value={value ?? ""}
        onChange={handleChange}
      />
    </div>
  );
};

export default PersonalIdentityNumberInput;
